import type { SalesDocumentSummary } from '@aeris/contracts'
import { formatDocumentNumber } from '@aeris/printing'
import type { StatusTone } from '@aeris/ui'

import { STATUS_META } from './sales-status-meta.tsx'

/**
 * Sales vocabulary, in Portuguese.
 *
 * `@aeris/sales` returns codes and never sentences: a pure package that the
 * browser and the server both import must not carry an interface language, and
 * a code is what a caller branches on. The words belong here, in one file, so
 * the list screen and the document editor cannot end up calling the same state
 * two different things.
 */

type Kind = SalesDocumentSummary['kind']
type Status = SalesDocumentSummary['status']
type StockState = SalesDocumentSummary['stockState']

export const KIND_LABELS: Record<Kind, string> = {
  quote: 'Orçamento',
  order: 'Pedido',
  sale: 'Venda',
}

/**
 * O rótulo de cada situação, DERIVADO do registro — F156.
 *
 * Era uma lista escrita à mão ao lado de outra lista de cores escrita à mão.
 * Agora as duas saem de `sales-status-meta.tsx`, que é onde mora também a
 * frase que explica cada uma. Duas listas paralelas divergem; uma derivada,
 * não.
 */
export const STATUS_LABELS: Record<Status, string> = Object.fromEntries(
  (Object.keys(STATUS_META) as Status[]).map((status) => [
    status,
    STATUS_META[status].label,
  ])
) as Record<Status, string>

/**
 * The verb on the button, which is not the same word as the state.
 *
 * "Enviar" is what the person does; "Enviado" is what the document then is.
 * Labelling a button with the resulting state — a button reading "Enviado" on a
 * document that has not been sent — is the small confusion that makes an
 * operator click twice.
 */
export const TRANSITION_LABELS: Record<Status, string> = {
  draft: 'Voltar para rascunho',
  sent: 'Enviar',
  accepted: 'Marcar como aceito',
  rejected: 'Marcar como recusado',
  expired: 'Marcar como vencido',
  confirmed: 'Confirmar',
  fulfilled: 'Dar saída',
  completed: 'Concluir',
  converted: 'Converter',
  cancelled: 'Cancelar',
}

export const STOCK_STATE_LABELS: Record<StockState, string> = {
  none: 'Sem efeito no estoque',
  reserved: 'Estoque reservado',
  consumed: 'Estoque baixado',
  released: 'Reserva liberada',
  reversed: 'Estoque estornado',
}

/** A cor de cada situação, derivada do mesmo registro. */
export const STATUS_TONES: Record<Status, StatusTone> = Object.fromEntries(
  (Object.keys(STATUS_META) as Status[]).map((status) => [
    status,
    STATUS_META[status].tone,
  ])
) as Record<Status, StatusTone>

/**
 * `Pedido nº 000012` — or `Pedido (rascunho)` while it has no number yet.
 *
 * Com os zeros (F248), como a lista, o papel e o `documentRef`: o título da
 * ficha dizia "Venda nº 16" ao lado de uma lista que dizia 000016, e quem
 * procurava o número digitado na ficha não achava o da lista.
 */
export const documentTitle = (document: {
  kind: Kind
  number: number | null
}): string =>
  document.number === null
    ? `${KIND_LABELS[document.kind]} (rascunho)`
    : `${KIND_LABELS[document.kind]} nº ${formatDocumentNumber(document.number)}`

/**
 * `Pedido nº 000018` — o número com o tipo, na forma da lista (F160).
 *
 * Toda tela que citava a origem de uma troca escrevia "Venda nº" à mão. Com a
 * troca aceitando pedido (F159), o Pedido 16 aparecia como "Venda nº 000016" ao
 * lado da Venda 16, e quem lia o cupom via duas linhas iguais.
 */
export const documentRef = (kind: Kind | null, number: number | null): string =>
  `${KIND_LABELS[kind ?? 'sale']} nº ${formatDocumentNumber(number)}`
